import { useState } from 'react';

/**
 * AddMosqueModal — lets users add a missing mosque
 * Pre-filled with lat/lng from map click or manual entry.
 * On submit: saves to D1 + sends OSM note automatically.
 */
export default function AddMosqueModal({ lat, lng, onClose, onSubmit, isDark = false }) {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const bgColor = isDark
    ? 'linear-gradient(180deg, rgba(30,30,30,0.97) 0%, rgba(15,15,15,0.99) 100%)'
    : 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)';
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const subTextColor = isDark ? '#94a3b8' : '#64748b';
  const inputBg = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)';
  const inputBorder = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
  const inputColor = isDark ? '#ffffff' : '#1f2937';

  const inputStyle = {
    width: '100%',
    padding: '12px 14px',
    background: inputBg,
    border: `1px solid ${inputBorder}`,
    borderRadius: '12px',
    marginBottom: 14,
    fontFamily: 'inherit',
    color: inputColor,
    fontSize: 14,
    boxSizing: 'border-box',
    outline: 'none',
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('মসজিদের নাম দিন');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({ name: name.trim(), lat, lng, address: address.trim() });
    } catch (err) {
      setError(err.message || 'কোনো সমস্যা হয়েছে, আবার চেষ্টা করুন');
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: isDark ? 'rgba(0,0,0,0.75)' : 'rgba(0,0,0,0.35)',
        backdropFilter: 'blur(10px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 3000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: bgColor,
          borderRadius: '24px',
          padding: '24px',
          width: '90%',
          maxWidth: '400px',
          border: `1px solid ${inputBorder}`,
          boxShadow: isDark
            ? '0 25px 50px -12px rgba(0,0,0,0.6)'
            : '0 25px 50px -12px rgba(0,0,0,0.15)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{
            width: 48, height: 48, borderRadius: '50%',
            background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 16px rgba(99,102,241,0.4)',
          }}>
            <span style={{ fontSize: 24 }}>🕌</span>
          </div>
          <div>
            <h2 style={{ color: textColor, fontSize: 18, fontWeight: 700, marginBottom: 2 }}>
              নতুন মসজিদ যোগ করুন
            </h2>
            <p style={{ color: subTextColor, fontSize: 12 }}>
              📍 {lat.toFixed(5)}, {lng.toFixed(5)}
            </p>
          </div>
        </div>

        {/* OSM note info */}
        <div style={{
          padding: '10px 14px', borderRadius: 12, marginBottom: 16,
          background: isDark ? 'rgba(99,102,241,0.1)' : 'rgba(99,102,241,0.06)',
          border: `1px solid ${isDark ? 'rgba(99,102,241,0.3)' : 'rgba(99,102,241,0.2)'}`,
        }}>
          <p style={{ color: isDark ? '#a5b4fc' : '#4f46e5', fontSize: 12, lineHeight: 1.5 }}>
            🗺️ আপনার যোগ করা মসজিদ স্বয়ংক্রিয়ভাবে <strong>OpenStreetMap</strong>-এ একটি note হিসেবে submit হবে — যাতে বিশ্বের map-এ যোগ হওয়ার সুযোগ থাকে।
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <label style={{ display: 'block', color: subTextColor, fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            মসজিদের নাম *
          </label>
          <input
            type="text"
            placeholder="যেমন: বায়তুল মোকাররম মসজিদ"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={inputStyle}
            autoFocus
          />

          <label style={{ display: 'block', color: subTextColor, fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            ঠিকানা (ঐচ্ছিক)
          </label>
          <input
            type="text"
            placeholder="যেমন: পুরানা পল্টন, ঢাকা"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            style={inputStyle}
          />

          {error && (
            <p style={{ color: '#ef4444', fontSize: 13, marginBottom: 12, fontWeight: 500 }}>
              ⚠️ {error}
            </p>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1, padding: '13px',
                background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                border: `1px solid ${inputBorder}`,
                borderRadius: '12px',
                color: subTextColor, cursor: 'pointer',
                fontWeight: 600, fontSize: 14,
              }}
            >
              বাতিল
            </button>
            <button
              type="submit"
              disabled={submitting}
              style={{
                flex: 2, padding: '13px',
                background: submitting
                  ? 'rgba(99,102,241,0.5)'
                  : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                color: 'white', border: 'none',
                borderRadius: '12px',
                fontWeight: 700, fontSize: 14,
                cursor: submitting ? 'not-allowed' : 'pointer',
                boxShadow: submitting ? 'none' : '0 4px 16px rgba(99,102,241,0.4)',
              }}
            >
              {submitting ? 'যোগ হচ্ছে...' : '✅ মসজিদ যোগ করুন'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
